import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import * as React from "react";
import HttpCheckoutRepository from "../api/request/Checkout/Checkout.service";
import HttpLeadRepository from "../api/request/Lead/Lead.service";
import { GetLifeTrackerContext } from "./GetLifeTracker.context";
import { useContext } from "react";
import { GetlifeContext } from "./GetLifeContext.context";
import { I18nContext } from "./i18n.context";
import { annualizePremium, CONVERSION_CURRENCY } from "../utils/Utils";
import { formatPhoneWithCallCode } from "../utils/InternationlChanges";
import { hashIdentity } from "../utils/hashIdentity";
import {
  trackPaymentFailed,
  trackPaymentMethodSaved,
  trackPaymentMethodSubmitted,
  trackSubmissionError,
  trackThreeDsCompleted,
  trackThreeDsRequested,
} from "../helpers/Tracking";

export type WalletTypeT = "card" | "google_pay" | "apple_pay";

interface ContextProps {
  handleSubmit: () => void;
  submitWalletPayment: (paymentMethodId: string, walletType: WalletTypeT) => Promise<boolean>;
}

export const StripeContext = React.createContext({} as ContextProps);

export const StripeProvider = ({
  handleIsLoading,
  setupIntent,
  children,
  leadId,
  token,
  checkout,
  navigate
}: any) => {
  const {
    setNotification,
    nodeElementAlert,
    utm_campaign,
    utm_source,
    utm_medium,
    utm_content,
    utm_term,
    utm_test,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion
  } = useContext(GetlifeContext);
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { handleTrackerQuestion } = React.useContext(GetLifeTrackerContext);
  const checkoutRepository = new HttpCheckoutRepository(leadId, token);
  const leadRepository = new HttpLeadRepository(leadId, token);

  const stripe = useStripe();
  const elements = useElements();

  const stripeErrors = [
    "card_declined",
    "expired_card",
    "incorrect_cvc",
    "processing_error",
  ];

  const message = (error: any) => {
    if (error.decline_code === "insufficient_funds") {
      return translate(`card.stripe.error.${error.decline_code}`);
    } else if (stripeErrors.includes(error.code)) {
      return translate(`card.stripe.error.${error.code}`);
    } else {
      return translate("card.stripe.error.generic");
    }
  };

  const fetchSavePayment = async (
    paymentMethodData: any,
    walletType: WalletTypeT = "card"
  ): Promise<boolean> => {
    try {
      const res = await checkoutRepository.savePaymentMethod(paymentMethodData);
      if (res !== "OK") {
        throw Object.assign(new Error("respuesta inesperada al guardar el método de pago"), {
          status: "unexpected_response",
        });
      }
      // Stripe y el backend han aceptado el método.
      trackPaymentMethodSaved(walletType);

      const paymentEvent = "paymentMethodSet";

      const leadInfo = leadId
        ? await leadRepository.getInfoLead().catch(() => null)
        : null;
      const identity = leadInfo
        ? await hashIdentity({
            email: leadInfo.email,
            phone: formatPhoneWithCallCode(leadInfo.phone),
          })
        : {};

      const paymentData = {
        ...identity,
        page: window.location.href,
        leadId: leadId,
        calculatedCapital: leadInfo?.capitals?.DEATH?.capital ?? parseFloat(checkout.capital),
        coverage: checkout.coverage === "premium" ? "FIPA" : "F",
        intent: checkout.intention,
        calculatedPremium: annualizePremium(checkout.regularPrice, checkout.period),
        paymentMethod: "card",
        walletType,
        utm_source: utm_source,
        utm_campaign: utm_campaign,
        utm_medium: utm_medium,
        utm_content: utm_content,
        utm_term: utm_term,
        utm_test: utm_test,
        gclid: gclid,
        gbraid: gbraid,
        wbraid: wbraid,
        msclkid: msclkid,
        fbclid: fbclid,
        platform: "App",
        discountedPremium: annualizePremium(checkout.priceToPay, checkout.period),
        paymentPeriod: checkout.period ? checkout.period.toLowerCase() : "monthly",
        currency: CONVERSION_CURRENCY,
        entryPage: getEntryPage(),
        referrerUrl: getReferrerUrl(),
        ABTestVersion: getABTestVersion()
      };

      handleTrackerQuestion({
        type: "GA",
        data: {
          event: paymentEvent,
          ...paymentData
        },
      });
      handleTrackerQuestion({
        type: "FS",
        event: paymentEvent,
        data: paymentData,
      });

      navigate();
      return true;
    } catch (error) {
      // Stripe aceptó la tarjeta pero el backend no guardó el método de pago.
      trackSubmissionError(error);
      setNotification({
        message: translate("card.stripe.error.generic"),
        type: "error",
        time: 5,
      });
      nodeElementAlert.current.handleShowPortal();
      handleIsLoading(false);
      return false;
    }
  };

  const handleSubmit = async () => {
    handleIsLoading(true);
    trackPaymentMethodSubmitted("card");

    if (!stripe || !elements) {
      return;
    }
    try {
      const result = await stripe.confirmCardSetup(setupIntent.clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: {
            name: setupIntent.name + " " + setupIntent.lastName,
          },
        },
      });

      if (result.error) {
        throw result.error;
      }
      const paymentMethodData = {
        providerId: result.setupIntent.payment_method,
        setupIntent: result.setupIntent.id,
        leadId: leadId,
      };
      await fetchSavePayment(paymentMethodData, "card");
      handleIsLoading(false);
      window.scrollTo(0, 0);
    } catch (error: any) {
      // Stripe rechaza la tarjeta en confirmCardSetup (decline_code) o falla:
      // solo payment_failed (antes también un error de envío: contaba doble).
      trackPaymentFailed("setup", error, "card");
      setNotification({
        message: message(error),
        type: "error",
        time: 5,
        positionY: "bottom",
      });
      nodeElementAlert.current.handleShowPortal();
      handleIsLoading(false);
    }
  };

  const submitWalletPayment = async (
    paymentMethodId: string,
    walletType: WalletTypeT
  ): Promise<boolean> => {
    handleIsLoading(true);
    trackPaymentMethodSubmitted(walletType);

    if (!stripe) {
      handleIsLoading(false);
      return false;
    }

    try {
      const result = await stripe.confirmCardSetup(
        setupIntent.clientSecret,
        { payment_method: paymentMethodId },
        { handleActions: false }
      );

      if (result.error) {
        throw result.error;
      }

      // Some setup intents require additional 3DS handling for wallet flows
      if (result.setupIntent?.status === "requires_action") {
        trackThreeDsRequested("setup");
        const action = await stripe.handleCardAction(setupIntent.clientSecret);
        trackThreeDsCompleted("setup", action.error ? "failed" : "succeeded");
        if (action.error) {
          throw action.error;
        }
      }

      const paymentMethodData = {
        providerId: result.setupIntent?.payment_method ?? paymentMethodId,
        setupIntent: result.setupIntent?.id,
        leadId: leadId,
      };

      const ok = await fetchSavePayment(paymentMethodData, walletType);
      handleIsLoading(false);
      if (ok) window.scrollTo(0, 0);
      return ok;
    } catch (error: any) {
      trackPaymentFailed("setup", error, walletType);
      setNotification({
        message: message(error),
        type: "error",
        time: 5,
        positionY: "bottom",
      });
      nodeElementAlert.current.handleShowPortal();
      handleIsLoading(false);
      return false;
    }
  };

  const values: ContextProps = {
    handleSubmit,
    submitWalletPayment,
  };

  return (
    <StripeContext.Provider value={values}>{children}</StripeContext.Provider>
  );
};
